import {
  Map as IMap,
} from 'immutable';
import {
  Accessor,
  Component,
  createMemo,
  Signal,
  useContext
} from 'solid-js';
import { Dynamic } from 'solid-js/web';
import Markdown from 'solid-marked/component';
import { ContentImage, ContentMarkdown } from './api-generated';
import { AuthContext } from './auth';
import { buffer2hex } from './utils';
import config from '../config';

type MarkdownBaseConfig = {
  level?: number;
  ugc?: boolean;
  linkInNewTab?: boolean;
};


type MarkdownRenderConfig = MarkdownBaseConfig & {
  markdown: Accessor<string>;
  lang: Accessor<string | undefined>;
  images: Accessor<IMap<string, MarkdownImage>>;
};

type MarkdownImage = ContentImage & {
};

const MarkdownRender: Component<MarkdownRenderConfig> = (props) =>
  <Markdown builtins={{
    Blockquote: (p) => <blockquote>{p.children}</blockquote>,
    Break: () => <br />,
    Code: (p) => <div class="code"><pre>{p.children}</pre></div>,
    Delete: (p) => <s>{p.children}</s>,
    Emphasis: (p) => <em>{p.children}</em>,
    Heading: (p) => <Dynamic component={`h${p.depth + (props.level ?? 0)}`}>{p.children}</Dynamic>,
    Image: (p) => {
      const info = createMemo<{
          url?: string;
          scale?: number;
          width?: number;
          height?: number;
        }>(() => {
        const image = props.images().get(p.url);
        if(!image) return {};
        const scale = (image.scale || 100) * 0.01;
        const width = image.width ? image.width / scale : undefined;
        const height = image.height ? image.height / scale : undefined;
        return {
          url: new URL(image.url, config.storageBaseUrl).toString(),
          scale,
          width,
          height,
        };
      });
      return <img
        srcset={`${info().url} ${info().scale}x`}
        width={info().width}
        height={info().height}
        alt={p.alt ?? undefined}
        title={p.title ?? undefined}
        loading="lazy"
      />;
    },
    InlineCode: (p) => <code>{p.children}</code>,
    Link: (p) => <a href={p.url} title={p.title ?? undefined} rel={(props.ugc ?? true) ? 'ugc nofollow' : undefined} target={props.linkInNewTab ? '_blank' : undefined}>{p.children}</a>,
    List: (p) => p.ordered ? <ol>{p.children}</ol> : <ul>{p.children}</ul>,
    ListItem: (p) => <li>{p.children}</li>,
    Paragraph: (p) => <p>{p.children}</p>,
    Root: (p) => <div class="markdown" lang={props.lang()} dir="auto">{p.children}</div>,
    Strong: (p) => <strong>{p.children}</strong>,
    ThematicBreak: () => <hr />,
  }}>{props.markdown()}</Markdown>
;


export type MarkdownViewConfig = MarkdownBaseConfig & {
  markdown: Accessor<ContentMarkdown>;
};

export const MarkdownView: Component<MarkdownViewConfig> = (props) => {
  const images = createMemo(() => IMap((props.markdown()?.images ?? []).map((image) => [image.id, image])));
  return <MarkdownRender
    {...props}
    markdown={() => props.markdown()?.markdown ?? ''}
    lang={() => props.markdown()?.lang}
    images={images}
  />;
}


export type MarkdownViewPlainTextLineConfig = {
  markdown: Accessor<string>;
  lang: Accessor<string | undefined>;
  classList?: {
    [k: string]: boolean | undefined;
  };
};

export const MarkdownViewPlainTextLine: Component<MarkdownViewPlainTextLineConfig> = (props) =>
  <Markdown builtins={{
    Blockquote: (p) => <>{' '}{p.children}{' '}</>,
    Break: () => ' ',
    Code: (p) => <>{' '}<code>{p.children}</code>{' '}</>,
    Delete: (p) => <s>{p.children}</s>,
    Emphasis: (p) => <em>{p.children}</em>,
    Heading: (p) => <>{' '}{p.children}{' '}</>,
    Image: (p) => ' ',
    InlineCode: (p) => <code>{p.children}</code>,
    Link: (p) => p.children,
    List: (p) => p.children,
    ListItem: (p) => <>{' '}{p.children}{' '}</>,
    Paragraph: (p) => <>{' '}{p.children}{' '}</>,
    Root: (p) => <div class="markdown" lang={props.lang()} dir="auto" classList={props.classList}>{p.children}</div>,
    Strong: (p) => <strong>{p.children}</strong>,
    ThematicBreak: () => ' ',
  }}>{props.markdown()}</Markdown>
;


export type MarkdownEditorConfig = MarkdownBaseConfig & {
  markdown: Signal<string>;
  lang: Signal<string | undefined>;
  images: Signal<IMap<string, MarkdownImage>>;
  projectId?: string;
};

export const MarkdownEditor: Component<MarkdownEditorConfig> = (props) => {
  const { api } = useContext(AuthContext)!;

  const [markdown, setMarkdown] = props.markdown;
  const [lang, setLang] = props.lang;
  const [images, setImages] = props.images;

  let ref: HTMLTextAreaElement;
  return <>
    <textarea
      ref={ref!}
      value={markdown()}
      lang={lang()}
      dir="auto"
      onInput={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setMarkdown(ref!.value);
      }}
      onPaste={async (e) => {
        // do not unnecessarily intercept paste event
        if(!props.projectId) return;
        if(!e.clipboardData) return;
        {
          let hasFiles = false;
          for(const item of e.clipboardData.items) {
            if(item.kind == 'file') {
              hasFiles = true;
              break;
            }
          }
          if(!hasFiles) return;
        }

        e.preventDefault();
        e.stopPropagation();

        for(const item of e.clipboardData.items)
        {
          const file = item.getAsFile();
          if(!file) continue;

          // convert image to JPEG via canvas
          const imageBitmap = await createImageBitmap(file);
          const imageWidth = imageBitmap.width;
          const imageHeight = imageBitmap.height;
          const imageScale = Math.floor(window.devicePixelRatio * 100);
          const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
          const context = canvas.getContext('bitmaprenderer');
          context!.transferFromImageBitmap(imageBitmap);
          imageBitmap.close();
          const fileJpeg = await canvas.convertToBlob({
            type: 'image/jpeg',
          });

          // create content image on backend
          const newContentImage = await api.postProjectsContentImages({
            project: props.projectId,
            requestBody: {
              size: fileJpeg.size,
              width: imageWidth,
              height: imageHeight,
              scale: imageScale,
            },
          });

          // add image to images map and markdown
          setImages((images) => {
            const imageId = newContentImage.id;
            ref!.setRangeText(`![image](${imageId})`, ref!.selectionStart, ref!.selectionEnd, 'end');
            return images.set(imageId, {
              id: imageId,
              width: imageWidth,
              height: imageHeight,
              scale: imageScale,
              url: URL.createObjectURL(fileJpeg),
            });
          });
          setMarkdown(ref!.value);

          // perform upload
          let partToken: string | undefined;
          let progress = 0, total = 0;
          const hash = buffer2hex(await crypto.subtle.digest('SHA-1', await fileJpeg.arrayBuffer()));
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.responseType = 'json';
            xhr.addEventListener('error', reject);
            xhr.addEventListener('load', () => {
              if(xhr.status !== 200) return reject(xhr.response);
              partToken = xhr.response;
              resolve();
            });
            xhr.upload.addEventListener('progress', (e) => {
              if(e.lengthComputable) {
                progress = Number(e.loaded);
                total = Number(e.total);
              }
            });

            xhr.open('PUT', (() => {
              const url = new URL(newContentImage.uploadInfo.uploadUrl, config.storageBaseUrl);
              url.searchParams.set('part', '0');
              url.searchParams.set('hash', hash);
              return url.toString();
            })(), true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            // no Content-Length header, it's forbidden in browsers
            xhr.send(fileJpeg);
          });
          // finalize image
          const contentImage = await api.putProjectsContentImagesFinalize({
            project: props.projectId,
            contentImage: newContentImage.id,
            requestBody: {
              token: newContentImage.uploadInfo.token,
              parts: [partToken!],
            },
          });
          // replace image URL
          setImages((images) => images.update(contentImage.id, (image) => {
            URL.revokeObjectURL(image!.url);
            return {
              ...image!,
              url: contentImage.url,
            };
          }));
        }
      }}
      rows={10}
    />
    <MarkdownRender
      {...props}
      markdown={markdown}
      lang={lang}
      images={images}
    />
  </>;
};


export const ContentImageView: Component<{
  image: ContentImage;
}> = (props) => {
  const info = createMemo<{
      url?: string;
      scale?: number;
      width?: number;
      height?: number;
    }>(() => {
    const image = props.image;
    if(!image) return {};
    const scale = (image.scale || 100) * 0.01;
    const width = image.width ? image.width / scale : undefined;
    const height = image.height ? image.height / scale : undefined;
    return {
      url: new URL(image.url, config.storageBaseUrl).toString(),
      scale,
      width,
      height,
    };
  });
  return <img
    srcset={`${info().url} ${info().scale}x`}
    width={info().width}
    height={info().height}
    loading="lazy"
  />;
};
